import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Chip, Divider, CircularProgress,
  Alert, Collapse, IconButton, Tooltip,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export default function CoachingPanel({ coaching, isLoading, activeSector }) {
  const { t } = useTranslation('race_engineer');
  const [expandedIdx, setExpandedIdx] = useState(null);

  const PRIORITY_CONFIG = {
    1: { label: t('coaching.priority_high'),   color: 'error',   icon: <PriorityHighIcon sx={{ fontSize: 14 }} /> },
    2: { label: t('coaching.priority_medium'), color: 'warning', icon: <TipsAndUpdatesIcon sx={{ fontSize: 14 }} /> },
    3: { label: t('coaching.priority_low'),    color: 'success', icon: <InfoOutlinedIcon sx={{ fontSize: 14 }} /> },
  };

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress color="secondary" size={36} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {t('coaching.analyzing')}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {t('coaching.processing')}
        </Typography>
      </Paper>
    );
  }

  if (!coaching) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(63,81,181,0.1)', borderRadius: 2, display: 'flex' }}>
            <SpeedIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{t('coaching.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('coaching.empty_message')}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  const consigli = coaching.consigli ?? [];
  const usage    = coaching._usage;

  const filtered = activeSector
    ? consigli.filter(c => {
        const n = parseInt(c.curva?.replace(/\D/g, '') ?? '0');
        return n === activeSector;
      })
    : consigli;

  const displayed = filtered.length > 0 ? filtered : consigli;
  const sorted    = [...displayed].sort((a, b) => (a.priorita ?? 9) - (b.priorita ?? 9));

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <Box sx={{
        px: 2.5, py: 1.75,
        borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex', alignItems: 'center', gap: 1.5,
        '@media print': { bgcolor: 'white' },
      }}>
        <Box sx={{ p: 0.75, bgcolor: 'rgba(63,81,181,0.12)', borderRadius: 1.5, display: 'flex' }}>
          <SpeedIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={700}>{t('coaching.coach_title')}</Typography>
        {activeSector && (
          <Chip label={`${t('coaching.sector_label')} ${activeSector}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: 10 }} />
        )}
        {usage && (
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {((usage.input_tokens + usage.output_tokens) / 1000).toFixed(1)}k token
            {usage.cache_read_tokens > 0 && ` · ${(usage.cache_read_tokens/1000).toFixed(1)}k cache`}
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        {/* ── Sommario ── */}
        {coaching.sommario && (
          <Alert
            severity="info"
            icon={<TipsAndUpdatesIcon />}
            sx={{
              mb: 2,
              '& .MuiAlert-message': { fontSize: 13, lineHeight: 1.7 },
              '@media print': { bgcolor: '#f0f4ff', border: '1px solid #b0c0e0' },
            }}
          >
            {coaching.sommario}
          </Alert>
        )}

        {/* Hint click-to-filter — solo se nessun settore attivo */}
        {!activeSector && sorted.length > 1 && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
            {t('coaching.click_sector_hint', { defaultValue: '↑ Clicca un settore nella tabella per filtrare i consigli' })}
          </Typography>
        )}

        {/* ── Consigli ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sorted.map((c, i) => {
            const pConfig  = PRIORITY_CONFIG[c.priorita] ?? PRIORITY_CONFIG[2];
            const expanded = expandedIdx === i;
            return (
              <Box
                key={i}
                className="consiglio-card"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'background.default',
                  '@media print': { bgcolor: '#fafafa', breakInside: 'avoid', pageBreakInside: 'avoid' },
                }}
              >
                {/* Card header — cliccabile per espandere causa */}
                <Box
                  onClick={() => setExpandedIdx(expanded ? null : i)}
                  sx={{ px: 1.5, py: 1, borderBottom: expanded ? '1px solid' : 'none', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Chip label={c.curva} size="small" color="secondary" sx={{ fontWeight: 700, fontSize: 11 }} />
                  <Chip icon={pConfig.icon} label={pConfig.label} size="small" color={pConfig.color} variant="outlined" sx={{ fontSize: 10 }} />
                  {c.problema && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', flex: 1, minWidth: 0 }}>
                      {c.problema}
                    </Typography>
                  )}
                  <Tooltip title={expanded ? 'Comprimi' : 'Espandi causa'}>
                    <IconButton size="small" sx={{ ml: 'auto', flexShrink: 0 }}>
                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Consiglio — sempre visibile */}
                <Box sx={{ px: 1.5, py: 1.25 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary', fontWeight: 500 }}>
                    {c.consiglio}
                  </Typography>
                </Box>

                {/* Causa probabile — collassata di default, espande al click */}
                <Collapse in={expanded}>
                  {c.causa_probabile && (
                    <Box sx={{ px: 1.5, pb: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', pt: 1 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                          {c.causa_probabile}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </Box>
            );
          })}
        </Box>

        {displayed.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t('coaching.no_tips_sector')}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
